<template>
  <div class="sales-management">
    <div class="page-header">
      <h1>{{ $t("sales.title") }}</h1>
      <p class="subtitle">{{ $t("sales.subtitle") }}</p>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="search-section">
      <div class="search-form">
        <div class="form-group">
          <label>{{ $t("sales.dateRange") }}</label>
          <input v-model="filters.startDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t("sales.to") }}</label>
          <input v-model="filters.endDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t('navigation.products') }}</label>
          <select v-model="filters.productId" class="form-control">
            <option value="">{{ $t('common.all') }}{{ $t('navigation.products') }}</option>
            <option v-for="product in products" :key="product.id" :value="product.id">
              {{ product.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t("sales.salesman") }}</label>
          <select v-model="filters.salesman" class="form-control">
            <option value="">{{ $t('common.all') }}销售员</option>
            <option v-for="staff in staffList" :key="staff.id" :value="staff.id">
              {{ staff.name }}
            </option>
          </select>
        </div>
        <button class="btn btn-primary" @click="searchSales">
          <i class="icon-search"></i> {{ $t("sales.search") }}
        </button>
        <button class="btn btn-secondary" @click="resetFilters">{{ $t('common.reset') }}</button>
      </div>
      <div class="actions">
        <button class="btn btn-primary" @click="showNewSaleModal">
          <i class="icon-plus"></i> {{ $t("sales.newSale") }}
        </button>
        <button class="btn btn-secondary" @click="exportSales">
          <i class="icon-download"></i> {{ $t("sales.export") }}
        </button>
      </div>
    </div>
    
    <!-- 销售统计 -->
    <div class="statistics-bar">
      <div class="stat-item">
        <span class="label">{{ $t("sales.totalAmount") }}：</span>
        <span class="value">¥{{ totalSalesAmount }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("sales.totalCount") }}：</span>
        <span class="value">{{ totalSalesCount }} {{ $t("sales.records") }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("sales.avgPrice") }}：</span>
        <span class="value">¥{{ averagePrice }}</span>
      </div>
      <div class="stat-item">
        <span class="label">总{{ $t('navigation.products') }}数：</span>
        <span class="value">{{ totalItems }} {{ $t("sales.items") }}</span>
      </div>
    </div>
    
    <!-- 销售列表 -->
    <div class="table-container">
      <table class="sales-table">
        <thead>
          <tr>
            <th width="60">{{ $t("sales.seq") }}</th>
            <th width="120">{{ $t("sales.date") }}</th>
            <th width="100">{{ $t('navigation.products') }}名称</th>
            <th width="80">{{ $t('navigation.products') }}编码</th>
            <th width="80">销售{{ $t('common.quantity') }}</th>
            <th width="80">{{ $t("sales.unitPrice") }}</th>
            <th width="100">销售{{ $t('common.amount') }}</th>
            <th width="100">{{ $t("sales.salesman") }}</th>
            <th width="80">{{ $t('common.status') }}</th>
            <th width="150">{{ $t('common.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="salesList.length === 0">
            <td colspan="10" class="empty-cell">
              <div class="empty-state">
                <i class="icon-inbox"></i>
                <p>{{ $t("sales.noRecords") }}</p>
              </div>
            </td>
          </tr>
          <tr v-for="(sale, index) in salesList" :key="sale.id">
            <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
            <td>{{ formatDate(sale.createdAt) }}</td>
            <td>
              <div class="product-cell">
                <div class="product-name">{{ sale.productName }}</div>
              </div>
            </td>
            <td>{{ sale.productCode }}</td>
            <td>{{ sale.quantity }}</td>
            <td>¥{{ sale.unitPrice }}</td>
            <td class="amount">¥{{ sale.totalAmount }}</td>
            <td>{{ sale.salesmanName }}</td>
            <td>
              <span :class="['status', `status-${sale.status}`]">
                {{ statusLabels[sale.status] }}
              </span>
            </td>
            <td>
              <button class="btn-action" @click="viewSaleDetail(sale.id)">
                <i class="icon-eye"></i> {{ $t("sales.view") }}
              </button>
              <button class="btn-action" @click="editSale(sale.id)">
                <i class="icon-edit"></i> {{ $t('common.edit') }}
              </button>
              <button class="btn-action danger" @click="deleteSale(sale.id)">
                <i class="icon-trash"></i> {{ $t('common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          {{ totalCount }} {{ $t('sales.records') }}，
          <select v-model.number="pageSize" @change="searchSales" class="page-size-select">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="pagination-controls">
          <button :disabled="currentPage === 1" @click="currentPage--" class="btn-page">
            <i class="icon-chevron-left"></i> {{ $t("pagination.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="currentPage++" class="btn-page">
            {{ $t("pagination.next") }} <i class="icon-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 数量选择弹窗 -->
    <div v-if="showQuantityPicker" class="modal-overlay modal-show" @click.self="closeQuantityPicker">
      <div class="modal quantity-picker-modal">
        <div class="modal-header">
          <h2>{{ $t('Stall.Sales.Index.line158_text') }}</h2>
          <button class="close-btn" @click="closeQuantityPicker">×</button>
        </div>
        <div class="modal-body">
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div class="picker-scroll" ref="quantityPickerScroll" @scroll="onQuantityPickerScroll">
              <div class="picker-item" v-for="qty in quantityOptions" :key="qty">{{ qty }}</div>
            </div>
          </div>
          <div class="quantity-input-section">
            <label>{{ $t('Stall.Sales.Index.line169_text') }}</label>
            <input type="number" v-model="customQuantity" min="1" step="1" class="quantity-input" @input="onCustomQuantityInput" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn primary" @click="confirmQuantity">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 价格选择弹窗 -->
    <div v-if="showPricePicker" class="modal-overlay modal-show" @click.self="closePricePicker">
      <div class="modal price-picker-modal">
        <div class="modal-header">
          <h2>{{ $t('Stall.Sales.Index.line183_text') }}</h2>
          <button class="close-btn" @click="closePricePicker">×</button>
        </div>
        <div class="modal-body">
          <div class="price-input-section">
            <label>¥</label>
            <input type="number" v-model="customPrice" min="0" step="0.01" class="price-input" placeholder="0.00" />
          </div>
          <div class="price-reference">
            <div class="price-ref-item">
              <span class="price-ref-label">{{ $t('Stall.Sales.Index.line193_text') }}</span>
              <span class="price-ref-value">¥{{ selectedProductPrice ? selectedProductPrice.toFixed(2) : '0.00' }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn primary" @click="confirmPrice">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 新增销售单模态框 -->
    <div v-if="showSaleModal" class="modal-overlay modal-show" @click="closeSaleModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h2>{{ saleFormTitle }}</h2>
          <button class="btn-close" @click="closeSaleModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitSale">
            <div class="form-group">
              <label>{{ $t('navigation.products') }} <span class="required">*</span></label>
              <select v-model="saleForm.productId" class="form-control" required>
                <option value="">{{ $t('common.select') }}{{ $t('navigation.products') }}</option>
                <option v-for="product in products" :key="product.id" :value="product.id">
                  {{ product.name }} - ¥{{ product.salePrice }} (库存: {{ product.stock }})
                </option>
              </select>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>销售{{ $t('common.quantity') }} <span class="required">*</span></label>
                <div class="picker-trigger" @click="openQuantityPicker">
                  <span class="picker-value">{{ saleForm.quantity || 0 }}</span>
                  <span class="picker-arrow">▾</span>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('sales.unitPrice') }} <span class="required">*</span></label>
                <div class="picker-trigger" @click="openPricePicker">
                  <span class="picker-value">¥{{ saleForm.unitPrice ? saleForm.unitPrice.toFixed(2) : '0.00' }}</span>
                  <span class="picker-arrow">▾</span>
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ $t('sales.salesman') }} <span class="required">*</span></label>
              <select v-model="saleForm.salesmanId" class="form-control" required>
                <option value="">{{ $t('common.select') }}销售员</option>
                <option v-for="staff in staffList" :key="staff.id" :value="staff.id">
                  {{ staff.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>{{ $t('common.remark') }}</label>
              <textarea v-model="saleForm.remarks" class="form-control" rows="3" :placeholder="$t('sales.inputRemark')"></textarea>
            </div>
            
            <div class="form-group total">
              <label>销售{{ $t('common.amount') }}：</label>
              <span class="total-amount">¥{{ calculateTotal() }}</span>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeSaleModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ saleForm.id ? $t('sales.update') : $t('sales.create') }}{{ $t('sales.saleForm') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SalesManagement',
  data() {
    return {
      filters: {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        salesman: ''
      },
      salesList: [],
      products: [],
      staffList: [],
      showSaleModal: false,
      saleForm: {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        salesmanId: '',
        remarks: ''
      },
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      statusLabel: {},
      totalSalesAmount: '0.00',
      totalSalesCount: 0,
      averagePrice: '0.00',
      totalItems: 0,
      
      // 数量选择弹窗
      showQuantityPicker: false,
      customQuantity: 1,
      
      // 价格选择弹窗
      showPricePicker: false,
      customPrice: 0,
      selectedProductPrice: 0
    };
  },
  computed: {
    ...mapGetters('stall', ['getStallInfo']),
    statusLabels() {
      return {
        completed: this.$t('sales.statusCompleted'),
        pending: this.$t('sales.statusPending'),
        cancelled: this.$t('sales.statusCancelled')
      };
    },
    saleFormTitle() {
      return this.saleForm.id ? this.$t('sales.editTitle') : this.$t('sales.newTitle');
    },
    quantityOptions() {
      const options = [];
      for (let i = 1; i <= 100; i++) {
        options.push(i);
      }
      return options;
    }
  },
  methods: {
    ...mapActions('stall', [
      'getSales',
      'getProducts',
      'getStaff',
      'createSale',
      'updateSale',
      'deleteSale',
      'getSaleDetail'
    ]),
    
    async loadSales() {
      try {
        const response = await this.getSales({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          productId: this.filters.productId,
          salesman: this.filters.salesman,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        this.salesList = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.calculateStatistics();
      } catch (error) {
        this.$message.error(this.$t('sales.loadFailed'));
      }
    },
    
    async loadProducts() {
      try {
        this.products = await this.getProducts();
      } catch (error) {
        console.error('加载商品失败', error);
      }
    },
    
    async loadStaff() {
      try {
        this.staffList = await this.getStaff();
      } catch (error) {
        console.error('加载员工列表失败', error);
      }
    },
    
    calculateStatistics() {
      if (this.salesList.length === 0) {
        this.totalSalesAmount = '0.00';
        this.totalSalesCount = 0;
        this.averagePrice = '0.00';
        this.totalItems = 0;
        return;
      }
      
      const total = this.salesList.reduce((sum, sale) => sum + parseFloat(sale.totalAmount), 0);
      const count = this.salesList.length;
      const items = this.salesList.reduce((sum, sale) => sum + sale.quantity, 0);
      
      this.totalSalesAmount = total.toFixed(2);
      this.totalSalesCount = count;
      this.averagePrice = (total / count).toFixed(2);
      this.totalItems = items;
    },
    
    searchSales() {
      this.currentPage = 1;
      this.loadSales();
    },
    
    resetFilters() {
      this.filters = {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        salesman: ''
      };
      this.currentPage = 1;
      this.loadSales();
    },
    
    showNewSaleModal() {
      this.saleForm = {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        salesmanId: '',
        remarks: ''
      };
      this.showSaleModal = true;
    },
    
    // 数量选择器方法
    openQuantityPicker() {
      this.customQuantity = this.saleForm.quantity || 1;
      this.showQuantityPicker = true;
      this.$nextTick(() => {
        const el = this.$refs.quantityPickerScroll;
        if (el) {
          const qty = this.saleForm.quantity || 1;
          const index = this.quantityOptions.indexOf(qty);
          if (index >= 0) {
            el.scrollTop = index * 32;
          }
        }
      });
    },
    
    closeQuantityPicker() {
      this.showQuantityPicker = false;
    },
    
    onQuantityPickerScroll() {
      if (this._quantityScrollTimer) {
        clearTimeout(this._quantityScrollTimer);
      }
      this._quantityScrollTimer = setTimeout(() => {
        const el = this.$refs.quantityPickerScroll;
        if (!el) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        if (this.quantityOptions[index] !== undefined) {
          this.customQuantity = this.quantityOptions[index];
        }
      }, 100);
    },
    
    onCustomQuantityInput() {
      const qty = parseInt(this.customQuantity);
      if (isNaN(qty) || qty <= 0) return;
      const el = this.$refs.quantityPickerScroll;
      if (el) {
        const index = this.quantityOptions.indexOf(qty);
        if (index >= 0) {
          el.scrollTop = index * 32;
        }
      }
    },
    
    confirmQuantity() {
      if (this.customQuantity > 0) {
        this.saleForm.quantity = this.customQuantity;
      }
      this.closeQuantityPicker();
    },
    
    // 价格选择器方法
    openPricePicker() {
      this.customPrice = this.saleForm.unitPrice || 0;
      // 获取选中商品的参考价
      if (this.saleForm.productId) {
        const product = this.products.find(p => p.id === this.saleForm.productId);
        this.selectedProductPrice = product ? (product.salePrice || 0) : 0;
      } else {
        this.selectedProductPrice = 0;
      }
      this.showPricePicker = true;
    },
    
    closePricePicker() {
      this.showPricePicker = false;
    },
    
    confirmPrice() {
      if (this.customPrice >= 0) {
        this.saleForm.unitPrice = this.customPrice;
      }
      this.closePricePicker();
    },
    
    closeSaleModal() {
      this.showSaleModal = false;
    },
    
    async editSale(saleId) {
      try {
        const sale = await this.getSaleDetail(saleId);
        this.saleForm = {
          id: sale.id,
          productId: sale.productId,
          quantity: sale.quantity,
          unitPrice: sale.unitPrice,
          salesmanId: sale.salesmanId,
          remarks: sale.remarks
        };
        this.showSaleModal = true;
      } catch (error) {
        this.$message.error(this.$t('sales.loadDetailFailed'));
      }
    },
    
    async submitSale() {
      try {
        if (this.saleForm.id) {
          await this.updateSale({ id: this.saleForm.id, ...this.saleForm });
          this.$message.success(this.$t('sales.updateSuccess'));
        } else {
          await this.createSale(this.saleForm);
          this.$message.success(this.$t('sales.createSuccess'));
        }
        this.closeSaleModal();
        this.loadSales();
      } catch (error) {
        this.$message.error(this.$t('sales.opFailed'));
      }
    },
    
    async deleteSale(saleId) {
      if (!confirm(this.$t('sales.deleteConfirm'))) {
        return;
      }
      
      try {
        await this.deleteSale(saleId);
        this.$message.success(this.$t('sales.deleteSuccess'));
        this.loadSales();
      } catch (error) {
        this.$message.error(this.$t('sales.deleteFailed'));
      }
    },
    
    viewSaleDetail(saleId) {
      this.$router.push(`/stall/sales/${saleId}`);
    },
    
    calculateTotal() {
      return (this.saleForm.quantity * this.saleForm.unitPrice).toFixed(2);
    },
    
    exportSales() {
      this.$message.success(this.$t('sales.exporting'));
      // 实现导出功能
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    
    getDateString(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  
  mounted() {
    this.loadProducts();
    this.loadStaff();
    this.loadSales();
  }
};
</script>

<style scoped lang="scss">
// 数量/价格选择弹窗样式
.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: #fff;
  &:hover {
    border-color: #3498db;
  }
  .picker-value {
    font-weight: 600;
    color: #333;
  }
  .picker-arrow {
    font-size: 10px;
    color: #999;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.quantity-picker-modal,
.price-picker-modal {
  background: white;
  border-radius: 12px;
  width: 300px;
  max-width: 90%;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    h2 {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #999;
    }
  }
  .modal-body {
    margin-bottom: 16px;
  }
  .modal-footer {
    display: flex;
    justify-content: flex-end;
  }
}

.quantity-picker {
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.picker-highlight {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 32px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  opacity: 0.15;
  border-radius: 4px;
  pointer-events: none;
  z-index: 2;
}

.picker-scroll {
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.picker-item {
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 16px;
  color: #666;
  scroll-snap-align: center;
  user-select: none;
  &:first-child {
    padding-top: 64px;
  }
  &:last-child {
    padding-bottom: 64px;
  }
}

.quantity-input-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  label {
    font-size: 14px;
    color: #666;
    flex-shrink: 0;
  }
}

.quantity-input {
  width: 80px;
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  &:focus {
    outline: none;
    border-color: #3498db;
  }
}

.price-input-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
  label {
    font-size: 16px;
    font-weight: 600;
    color: #666;
  }
}

.price-input {
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #3498db;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #e74c3c;
  text-align: center;
  outline: none;
  &:focus {
    border-color: #3498db;
  }
}

.price-reference {
  padding: 12px;
  background: #fff3e0;
  border-radius: 8px;
}

.price-ref-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-ref-label {
  font-size: 13px;
  color: #666;
}

.price-ref-value {
  font-size: 15px;
  font-weight: 600;
  color: #e74c3c;
}

.modal-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
  &.primary {
    background: #3498db;
    color: white;
  }
  &:hover {
    opacity: 0.9;
  }
}

.sales-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  
  .page-header {
    margin-bottom: 20px;
    h1 {
      font-size: 24px;
      font-weight: bold;
      color: #2c3e50;
      margin: 0 0 8px 0;
    }
    .subtitle {
      color: #999;
      font-size: 13px;
      margin: 0;
    }
  }
  
  // 搜索和筛选
  .search-section {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    .search-form {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 16px;
      
      .form-group {
        display: flex;
        flex-direction: column;
        label {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 4px;
        }
        .form-control {
          padding: 8px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
          &:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
          }
        }
      }
      
      button {
        align-self: flex-end;
      }
    }
    
    .actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      button {
        min-width: 120px;
      }
    }
  }
  
  // 统计栏
  .statistics-bar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    
    .stat-item {
      background: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .label {
        font-size: 12px;
        color: #999;
        margin-right: 8px;
      }
      .value {
        font-size: 16px;
        font-weight: bold;
        color: #3498db;
      }
    }
  }
  
  // 表格容器
  .table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .sales-table {
      width: 100%;
      border-collapse: collapse;
      
      thead {
        background-color: #f5f7fa;
        border-bottom: 2px solid #ddd;
        th {
          padding: 12px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #666;
        }
      }
      
      tbody {
        tr {
          border-bottom: 1px solid #eee;
          transition: background-color 0.3s ease;
          
          &:hover {
            background-color: #f9f9f9;
          }
          
          td {
            padding: 12px;
            font-size: 12px;
            color: #666;
            
            &.empty-cell {
              padding: 0;
            }
            
            &.amount {
              color: #3498db;
              font-weight: 600;
            }
            
            .status {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 500;
              
              &.status-completed {
                background-color: #d4edda;
                color: #155724;
              }
              &.status-pending {
                background-color: #fff3cd;
                color: #856404;
              }
              &.status-cancelled {
                background-color: #f8d7da;
                color: #721c24;
              }
            }
            
            .product-cell {
              .product-name {
                font-weight: 500;
                color: #2c3e50;
              }
            }
          }
        }
      }
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      
      i {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 16px;
      }
      p {
        color: #999;
        font-size: 14px;
        margin: 0;
      }
    }
  }
  
  // 表格操作按钮
  .btn-action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    margin-right: 4px;
    background: none;
    border: none;
    color: #3498db;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: #2980b9;
    }
    
    &.danger {
      color: #e74c3c;
      &:hover {
        color: #c0392b;
      }
    }
    
    i {
      font-size: 13px;
    }
  }
  
  // 分页
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #eee;
    background: white;
    
    .pagination-info {
      font-size: 12px;
      color: #666;
      
      .page-size-select {
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 0 6px;
        font-size: 12px;
        background: white;
      }
    }
    
    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .btn-page {
        padding: 6px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover:not(:disabled) {
          border-color: #3498db;
          color: #3498db;
        }
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
      
      .page-info {
        font-size: 12px;
        color: #666;
        min-width: 60px;
        text-align: center;
      }
    }
  }
  
  // 模态框
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    
    .modal-dialog {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        
        h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
        
        .btn-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          
          &:hover {
            color: #2c3e50;
          }
        }
      }
      
      .modal-body {
        padding: 20px;
        
        form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .form-group {
            display: flex;
            flex-direction: column;
            
            label {
              font-size: 12px;
              font-weight: 500;
              color: #666;
              margin-bottom: 6px;
              
              .required {
                color: #e74c3c;
              }
            }
            
            .form-control {
              padding: 10px 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 12px;
              font-family: inherit;
              
              &:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
              }
            }
            
            &.total {
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              padding: 12px;
              background-color: #f5f7fa;
              border-radius: 4px;
              
              label {
                margin: 0;
              }
              
              .total-amount {
                font-size: 18px;
                font-weight: bold;
                color: #3498db;
              }
            }
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          
          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 20px;
            
            button {
              min-width: 100px;
            }
          }
        }
      }
    }
  }
  
  // 通用按钮样式
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.btn-primary {
      background-color: #3498db;
      color: white;
      
      &:hover {
        background-color: #2980b9;
      }
    }
    
    &.btn-secondary {
      background-color: #ecf0f5;
      color: #666;
      
      &:hover {
        background-color: #d5dce3;
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>